import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { SubscriptionCreateRequest, SubscriptionResourceType, SubscriptionNotificationType } from '@/api/bol-notifications';
import { getMembersApi } from '@/services/bolApiClient';
import { Member } from '@/api/bol';
import { PrimaryButton, SecondaryButton } from '@/components/index';
import JsonEditor from '@/components/common/JsonEditor';
import { LOGISTICS_OBJECT_ENTITIES, getLogisticsObjectTypeTokens } from '@/types/logisticsObject';

export interface SubscriptionCreateDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: SubscriptionCreateRequest) => Promise<void>;
}

interface FormData {
  name: string;
  memberIdentifier: string;
  notificationType: SubscriptionNotificationType;
  resourceType: SubscriptionResourceType;
  resourceId: string;
  query?: string; // JSON string
}

type SelectOption<TValue> = {
  value: TValue;
  label: string;
};

const resourceTypeOptions: SelectOption<SubscriptionResourceType>[] = LOGISTICS_OBJECT_ENTITIES.map((entity) => {
  const { singularPascal, singularDisplay } = getLogisticsObjectTypeTokens(entity);

  return {
    value: singularPascal as SubscriptionResourceType,
    label: singularDisplay,
  };
});

const notificationTypeOptions: SelectOption<SubscriptionNotificationType>[] = [
  { value: SubscriptionNotificationType.Updates, label: 'Updates' },
  { value: SubscriptionNotificationType.Events, label: 'Events' },
  { value: SubscriptionNotificationType.Acl, label: 'ACL' },
];

const defaultFormValues: FormData = {
  name: '',
  memberIdentifier: '',
  notificationType: SubscriptionNotificationType.Updates,
  resourceType: resourceTypeOptions[0].value,
  resourceId: '',
  query: '',
};

export const SubscriptionCreateDialog: React.FC<SubscriptionCreateDialogProps> = ({
  open,
  onClose,
  onSubmit,
}) => {
  const form = useForm<FormData>({
    defaultValues: defaultFormValues,
  });

  const { isSubmitting } = form.formState;
  const [members, setMembers] = useState<Member[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [queryError, setQueryError] = useState<string | null>(null);

  const fetchMembers = useCallback(async () => {
    setMembersLoading(true);
    try {
      const api = getMembersApi();
      const response = await api.listMembers({ limit: 100 });
      setMembers(response.results);
    } catch (err: unknown) {
      console.error('Error fetching members:', err);
    } finally {
      setMembersLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      form.reset(defaultFormValues);
      setQueryError(null);
      fetchMembers();
    }
  }, [open, form, fetchMembers]);

  const onFormSubmit = async (data: FormData) => {
    try {
      // Validate and parse query if provided
      let parsedQuery: object | undefined = undefined;
      if (data.query && data.query.trim() !== '') {
        try {
          parsedQuery = JSON.parse(data.query);
          setQueryError(null);
        } catch {
          setQueryError('Invalid JSON format');
          return;
        }
      }

      const createData: SubscriptionCreateRequest = {
        name: data.name,
        memberIdentifier: data.memberIdentifier,
        notificationType: data.notificationType,
        resourceType: data.resourceType,
        resourceId: data.resourceId || undefined,
        query: parsedQuery,
      };
      
      await onSubmit(createData);
      handleClose();
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  const handleClose = () => {
    form.reset();
    setMembers([]);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create New Subscription</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }} component="form" onSubmit={form.handleSubmit(onFormSubmit)}>
          <Controller
            name="name"
            control={form.control}
            rules={{ 
              required: 'Subscription name is required',
              validate: (value) => value.trim() !== '' || 'Subscription name cannot be empty'
            }}
            render={({ field }) => (
              <TextField
                {...field}
                autoFocus
                margin="dense"
                label="Subscription Name"
                fullWidth
                variant="outlined"
                required
                error={!!form.formState.errors.name}
                helperText={form.formState.errors.name?.message || "Enter a descriptive name for the subscription"}
                sx={{ mb: 2 }}
              />
            )}
          />
          
          <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
            <InputLabel>Member Identifier *</InputLabel>
            <Controller
              name="memberIdentifier"
              control={form.control}
              rules={{ required: 'Member identifier is required' }}
              render={({ field }) => (
                <Select
                  {...field}
                  label="Member Identifier *"
                  required
                  error={!!form.formState.errors.memberIdentifier}
                  disabled={membersLoading}
                  endAdornment={
                    <IconButton onClick={fetchMembers} disabled={membersLoading} sx={{ mr: 3 }}>
                      <Tooltip title="Refresh members">
                        <Refresh />
                      </Tooltip>
                    </IconButton>
                  }
                >
                  {membersLoading && <MenuItem disabled>Loading members...</MenuItem>}
                  {!membersLoading && (!members || members.length === 0) && <MenuItem disabled>No members available</MenuItem>}
                  {!membersLoading && members && members.length > 0 && members.map((member) => (
                    <MenuItem key={member.identifier} value={member.identifier}>
                      {member.identifier}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
            {form.formState.errors.memberIdentifier && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                {form.formState.errors.memberIdentifier.message}
              </Typography>
            )}
            {!form.formState.errors.memberIdentifier && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
                Select a member from the available list
              </Typography>
            )}
          </FormControl>

          <Controller
            name="notificationType"
            control={form.control}
            render={({ field }) => (
              <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
                <InputLabel>Notification Type</InputLabel>
                <Select {...field} label="Notification Type">
                  {notificationTypeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />

          <Controller
            name="resourceType"
            control={form.control}
            render={({ field }) => (
              <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
                <InputLabel>Resource Type</InputLabel>
                <Select {...field} label="Resource Type">
                  {resourceTypeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />

          <Controller
            name="resourceId"
            control={form.control}
            render={({ field }) => (
              <TextField
                {...field}
                margin="dense"
                label="Resource ID (Optional)"
                fullWidth
                variant="outlined"
                helperText="Specific resource ID to subscribe to (leave empty for all resources)"
                sx={{ mb: 2 }}
              />
            )}
          />

          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
              Query (Optional)
            </Typography>
            <Controller
              name="query"
              control={form.control}
              render={({ field }) => (
                <JsonEditor
                  value={field.value || ''}
                  onChange={(value) => {
                    field.onChange(value);
                    if (value && value.trim() !== '') {
                      try {
                        JSON.parse(value);
                        setQueryError(null);
                      } catch {
                        setQueryError('Invalid JSON format');
                      }
                    } else {
                      setQueryError(null);
                    }
                  }}
                  height={200}
                  error={queryError}
                  helperText="Enter a query expression in JSON format to filter notifications (leave empty for all resources)"
                />
              )}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <SecondaryButton onClick={handleClose} disabled={isSubmitting}>Cancel</SecondaryButton>
        <PrimaryButton onClick={form.handleSubmit(onFormSubmit)} type="submit" disabled={isSubmitting}>
          Create
        </PrimaryButton>
      </DialogActions>
    </Dialog>
  );
};
